import * as actionTypes from "../actionTypes";
import * as inputTypes from "../enums/inputTypes";
import * as searchUtil from "../lib/search/util";
import createReducer from "../lib/createReducer";
import findIndex from "lodash.findindex";

function insertAtIndex(arr, value, index) {
	return [...arr.slice(0, index), value, ...arr.slice(index)];
}

function setAtIndex(arr, value, index) {
	return [...arr.slice(0, index), value, ...arr.slice(index + 1)];
}

function removeAtIndex(arr, index) {
	return [...arr.slice(0, index), ...arr.slice(index + 1)];
}

function findValueInMatrix(matrix, search) {
	for (let i = 0; i < matrix.length; i++) {
		const column = matrix[i];
		for (let j = 0; j < column.length; j++) {
			const item = column[j];
			if (item === search) {
				return {
					found: true,
					outer: i,
					inner: j,
				};
			}
		}
	}

	return {
		found: false,
		outer: null,
		inner: null,
	};
}

function sum(arr) {
	return arr.reduce((total, cur) => total + cur, 0);
}

const defaultState = {};

function SEARCH_TAB_ADD(state, action) {
	const { icon, tabId, title, isSubsearch, profile } = action;

	const criterionIdsFullWidth = [];
	const criterionIdsDesktop = [[], [], []];
	const criterionIdsTablet = [[], []];
	const editorHeights = {};

	return {
		...state,
		[tabId]: {
			__id: tabId,
			criterionIdsFullWidth,
			criterionIdsDesktop,
			criterionIdsTablet,
			editorHeights,
			icon,
			title,
			isSubsearch,
			profile,
			loading: isSubsearch,
		},
	};
}

function SEARCH_TAB_SET_LOADING(state, action) {
	const { loading, tabId } = action;
	const tab = state[tabId];
	if (!tab) {
		return state;
	}

	return {
		...state,
		[tabId]: {
			...tab,
			loading,
		},
	};
}

function CRITERION_ADD(state, action) {
	const { criterionId, inputType, tabId, addAfterId } = action;
	const editorHeight = searchUtil.getEditorHeight(inputType);
	const tab = state[tabId];
	if (!tab) {
		console.warn(`tab with id "${tabId}" not found in state`);
		return state;
	}

	let columnIndexDesktop = null;
	let columnIndexTablet = null;
	let insertIndexDesktop = null;
	let insertIndexTablet = null;

	if (addAfterId) {
		const indexFullWidth = findIndex(tab.criterionIdsFullWidth, id => id === addAfterId);
		if (indexFullWidth !== -1) {
			const nextCriterionIdsFullWidth = [
				...tab.criterionIdsFullWidth.slice(0, indexFullWidth),
				criterionId,
				...tab.criterionIdsFullWidth.slice(indexFullWidth),
			];
			return {
				...state,
				[tabId]: {
					...tab,
					criterionIdsFullWidth: nextCriterionIdsFullWidth,
				},
			};
		}

		const resultDesktop = findValueInMatrix(tab.criterionIdsDesktop, addAfterId);
		if (resultDesktop.found) {
			columnIndexDesktop = resultDesktop.outer;
			insertIndexDesktop = resultDesktop.inner + 1;
		}

		const resultTablet = findValueInMatrix(tab.criterionIdsTablet, addAfterId);
		if (resultTablet.found) {
			columnIndexTablet = resultTablet.outer;
			insertIndexTablet = resultTablet.inner + 1;
		}
	}

	if (!addAfterId && __DEV__) {
		console.warn("Adding single criteria without after id");
	}

	let nextInnerDesktop;
	if (insertIndexDesktop === null) {
		// left to right fill
		columnIndexDesktop = 0;
		const columnHeights = tab.criterionIdsDesktop.map(columnCriterionIds =>
			sum(columnCriterionIds.map(cId => tab.editorHeights[cId])),
		);

		for (let i = 1; i < tab.criterionIdsDesktop.length; i++) {
			if (columnHeights[i] < columnHeights[columnIndexDesktop]) {
				columnIndexDesktop = i;
			}
		}

		nextInnerDesktop = [...tab.criterionIdsDesktop[columnIndexDesktop], criterionId];
	} else {
		// add after found criterion
		nextInnerDesktop = insertAtIndex(
			tab.criterionIdsDesktop[columnIndexDesktop],
			criterionId,
			insertIndexDesktop,
		);
	}

	let nextInnerTablet;
	if (insertIndexTablet === null) {
		// left to right fill
		columnIndexTablet = 0;
		const columnHeights = tab.criterionIdsTablet.map(columnCriterionIds =>
			sum(columnCriterionIds.map(cId => tab.editorHeights[cId])),
		);

		for (let i = 1; i < tab.criterionIdsTablet.length; i++) {
			if (columnHeights[i] < columnHeights[columnIndexTablet]) {
				columnIndexTablet = i;
			}
		}

		nextInnerTablet = [...tab.criterionIdsTablet[columnIndexTablet], criterionId];
	} else {
		// add after found criterion
		nextInnerTablet = insertAtIndex(
			tab.criterionIdsTablet[columnIndexTablet],
			criterionId,
			insertIndexTablet,
		);
	}

	const nextCriterionIdsDesktop = setAtIndex(
		tab.criterionIdsDesktop,
		nextInnerDesktop,
		columnIndexDesktop,
	);
	const nextCriterionIdsTablet = setAtIndex(
		tab.criterionIdsTablet,
		nextInnerTablet,
		columnIndexTablet,
	);

	const nextEditorHeights = {
		...tab.editorHeights,
		[criterionId]: editorHeight,
	};

	return {
		...state,
		[tabId]: {
			...tab,
			criterionIdsDesktop: nextCriterionIdsDesktop,
			criterionIdsTablet: nextCriterionIdsTablet,
			editorHeights: nextEditorHeights,
		},
	};
}

function CRITERION_ADD_MULTIPLE(state, action) {
	const { criteria } = action;
	if (!criteria.length) {
		return state;
	}

	let nextState = state;
	criteria.forEach(c => {
		nextState = CRITERION_ADD(nextState, c);
	});

	return nextState;
}

// NOTE(CK): This exists to add criterion to a search tab AND setup the layout for the criterion on
// that search tab. It considers multiple layouts based on the criterion editors heights and saves
// the layout with the shorest, tallest column. More details here:
// https://sleet.docketnavigator.com/projects/compass/work_packages/details/510
function CRITERION_ADD_MULTIPLE_LAYOUT_TAB(state, action) {
	const { criteria, tabId } = action;
	if (!criteria.length || tabId === null || tabId === undefined) {
		return state;
	}

	const tab = state[tabId];
	if (!tab) {
		console.warn(`tab with id "${tabId}" not found in state`);
		return state;
	}

	const fullWidthCriteria = criteria.filter(c => c.inputType === inputTypes.DFG);

	const criterionHeightArr = [];
	const editorHeights = {};
	criteria
		.filter(c => c.inputType !== inputTypes.DFG)
		.forEach(criterion => {
			const { criterionId, inputType } = criterion;
			const height = searchUtil.getEditorHeight(inputType);
			editorHeights[criterionId] = height;
			for (let i = 0; i < height; i++) {
				criterionHeightArr.push({
					id: criterionId,
					height,
				});
			}
		});

	const threeColumn = searchUtil.getThreeColumnLayout(criterionHeightArr);
	const twoColumn = searchUtil.getTwoColumnLayout(criterionHeightArr);

	const nextState = {
		...state,
		[tabId]: {
			...tab,
			criterionIdsFullWidth: fullWidthCriteria.map(c => c.criterionId),
			criterionIdsDesktop: threeColumn.ids,
			criterionIdsTablet: twoColumn.ids,
			editorHeights,
		},
	};
	return nextState;
}

function SEARCH_TAB_REMOVE_MULTIPLE(state, action) {
	const { tabIds } = action;
	const nextState = { ...state };
	tabIds.forEach(id => {
		delete nextState[id];
	});
	return nextState;
}

function CRITERION_REMOVE(state, action) {
	const { criterionId } = action;
	const nextState = { ...state };

	Object.keys(nextState).forEach(searchTabId => {
		const tab = nextState[searchTabId];

		const fullWidthIdx = findIndex(tab.criterionIdsFullWidth, id => id === criterionId);
		if (fullWidthIdx !== -1) {
			const nextCriterionIds = [
				...tab.criterionIdsFullWidth.slice(0, fullWidthIdx),
				...tab.criterionIdsFullWidth.slice(fullWidthIdx + 1),
			];
			nextState[searchTabId] = {
				...state[searchTabId],
				criterionIdsFullWidth: nextCriterionIds,
			};
			return;
		}

		const resultDesktop = findValueInMatrix(tab.criterionIdsDesktop, criterionId);
		if (resultDesktop.found) {
			const nextColumn = removeAtIndex(
				tab.criterionIdsDesktop[resultDesktop.outer],
				resultDesktop.inner,
			);
			const nextCriterionIds = setAtIndex(
				tab.criterionIdsDesktop,
				nextColumn,
				resultDesktop.outer,
			);

			nextState[searchTabId] = {
				...state[searchTabId],
				criterionIdsDesktop: nextCriterionIds,
			};
		}

		const resultTablet = findValueInMatrix(tab.criterionIdsTablet, criterionId);
		if (resultTablet.found) {
			const nextColumn = removeAtIndex(
				tab.criterionIdsTablet[resultTablet.outer],
				resultTablet.inner,
			);
			const nextCriterionIds = setAtIndex(
				tab.criterionIdsTablet,
				nextColumn,
				resultTablet.outer,
			);

			nextState[searchTabId] = {
				...nextState[searchTabId],
				criterionIdsTablet: nextCriterionIds,
			};
		}

		if (resultDesktop.found && resultTablet.found) {
			const nextEditorHeights = { ...tab.editorHeights };
			delete nextEditorHeights[criterionId];

			nextState[searchTabId].editorHeights = nextEditorHeights;
		}
	});

	return nextState;
}

function CRITERION_REMOVE_MULTIPLE(state, action) {
	const { criterionIds } = action;

	if (!criterionIds.length) {
		return state;
	}

	let nextState = state;
	criterionIds.forEach(c => {
		nextState = CRITERION_REMOVE(nextState, { criterionId: c });
	});

	return nextState;
}

const handlers = {};
handlers[actionTypes.SEARCH_TAB_ADD] = SEARCH_TAB_ADD;
handlers[actionTypes.SEARCH_TAB_REMOVE_MULTIPLE] = SEARCH_TAB_REMOVE_MULTIPLE;
handlers[actionTypes.SEARCH_TAB_SET_LOADING] = SEARCH_TAB_SET_LOADING;
handlers[actionTypes.CRITERION_ADD] = CRITERION_ADD;
handlers[actionTypes.CRITERION_ADD_MULTIPLE] = CRITERION_ADD_MULTIPLE;
handlers[actionTypes.CRITERION_ADD_MULTIPLE_LAYOUT_TAB] = CRITERION_ADD_MULTIPLE_LAYOUT_TAB;
handlers[actionTypes.CRITERION_REMOVE_MULTIPLE] = CRITERION_REMOVE_MULTIPLE;
handlers[actionTypes.CRITERION_REMOVE] = CRITERION_REMOVE;
export default createReducer(defaultState, handlers);
